/**
 * Header: transparent over the hero photo, deep burgundy once you scroll (and on every
 * other page). Brand = the crown + R emblem from the real logo with the name set beside it,
 * so it stays legible at navbar size. Nav labels in caps, "Book a table" always in reach.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import DrawerNuclear from './DrawerNuclear';
import { restaurantConfig } from '../config/restaurantConfig';
import { FaCalendarAlt } from 'react-icons/fa';
import emblem from '../assets/images/logoEmblem.webp';

interface HeaderProps {
  language: string;
  toggleLanguage: () => void;
  handleLinkClick?: () => void;
  isMobileMenuOpen?: boolean;
  toggleMobileMenu?: () => void;
  isScrolled?: boolean;
}

const SECTIONS = ['about', 'menu', 'gallery', 'testimonials', 'booking', 'contact'];

export const Header = ({ language, toggleLanguage, handleLinkClick = () => {} }: HeaderProps) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [active, setActive] = useState('home');
  const [bookingInView, setBookingInView] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const pt = language === 'pt';

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      setPastHero(!isHome || window.scrollY > window.innerHeight * 0.7);
      if (!isHome) return;
      if (window.scrollY < 200) return setActive('home');
      // the section whose top has passed ~35% of the screen is the one being read
      let current = 'home';
      for (const id of SECTIONS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.35) current = id;
      }
      setActive(current);
      const b = document.getElementById('booking');
      if (b) {
        const r = b.getBoundingClientRect();
        setBookingInView(r.top < window.innerHeight * 0.85 && r.bottom > window.innerHeight * 0.15);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const goTo = useCallback(
    (id: string, e?: React.MouseEvent) => {
      if (e) e.preventDefault();
      setMobileMenu(false);
      handleLinkClick();
      if (!isHome) {
        window.location.href = id === 'home' ? '/' : `/#${id}`;
        return;
      }
      if (id === 'home') window.scrollTo({ top: 0, behavior: 'smooth' });
      else document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    },
    [isHome, handleLinkClick],
  );

  const setLang = (l: 'en' | 'pt') => {
    if (l === language) return;
    localStorage.setItem('language', l);
    toggleLanguage();
  };

  const solid = scrolled || !isHome || mobileMenu;
  const links = [
    { id: 'home', en: 'Home', pt: 'Início' },
    { id: 'about', en: 'About Us', pt: 'Sobre Nós' },
    { id: 'menu-page', en: 'Menu', pt: 'Ementa' },
    { id: 'gallery', en: 'Gallery', pt: 'Galeria' },
    { id: 'testimonials', en: 'Reviews', pt: 'Avaliações' },
    { id: 'contact', en: 'Contact', pt: 'Contacto' },
  ];

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ric-header { transition: background-color .45s ease, box-shadow .45s ease, padding .45s ease; }
          .ric-header.is-clear { background: linear-gradient(to bottom, rgba(20,6,8,.72), rgba(20,6,8,0)); }
          .ric-header.is-solid { background: rgba(111,22,23,.97); box-shadow: 0 10px 30px -18px rgba(0,0,0,.6); backdrop-filter: blur(8px); }
          .ric-nav a { position: relative; font-family: 'Poppins', sans-serif; font-weight: 500; font-size: .78rem; letter-spacing: .16em; text-transform: uppercase; color: rgba(255,255,255,.9); padding: .5rem 0; transition: color .25s ease; }
          .ric-nav a::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 1.5px; background: #E4C77A; transform: scaleX(0); transition: transform .3s ease; }
          .ric-nav a:hover { color: #F3E79B; }
          .ric-nav a:hover::after, .ric-nav a.is-active::after { transform: scaleX(1); }
          .ric-nav a.is-active { color: #F3E79B; }
          .ric-book { font-family: 'Poppins', sans-serif; font-weight: 600; font-size: .74rem; letter-spacing: .14em; text-transform: uppercase; background: #C9A961; color: #3a0b0c; border-radius: 9999px; transition: background-color .25s ease, transform .25s ease; }
          .ric-book:hover { background: #E4C77A; transform: translateY(-1px); }
          .ric-lang button { font-family: 'Poppins', sans-serif; font-size: .7rem; font-weight: 600; letter-spacing: .12em; padding: .3rem .6rem; border-radius: 9999px; color: rgba(255,255,255,.75); transition: all .2s ease; }
          .ric-lang button.on { background: #C9A961; color: #3a0b0c; }
          .ric-pill { transition: opacity .4s ease, transform .4s ease; will-change: transform; backface-visibility: hidden; }
          .ric-pill.is-hidden { opacity: 0; transform: translate(-50%, 24px); pointer-events: none; }
        `,
        }}
      />
      <header className={`ric-header fixed top-0 left-0 w-full z-50 ${solid ? 'is-solid py-2' : 'is-clear py-4'}`}>
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-4 lg:px-8">
          {/* brand */}
          <a href="/" onClick={(e) => goTo('home', e)} className="flex shrink-0 items-center gap-3" aria-label={restaurantConfig.name}>
            <img src={emblem} alt="" width={312} height={478} className="h-12 w-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)] lg:h-14" />
            <span className="leading-none">
              <span className="block whitespace-nowrap font-['Playfair_Display',serif] text-[1.02rem] font-semibold uppercase tracking-[0.1em] text-white lg:text-[1.15rem]">
                Royal Indian Cuisine
              </span>
              <span className="mt-1 block font-['Poppins',sans-serif] text-[0.6rem] font-medium uppercase tracking-[0.28em] text-[#E4C77A]">
                {pt ? 'Restaurante Tandoori · Lagos' : 'Tandoori Restaurant · Lagos'}
              </span>
            </span>
          </a>

          {/* desktop nav */}
          <nav className="ric-nav hidden items-center gap-8 xl:flex" aria-label={pt ? 'Principal' : 'Main'}>
            {links.map((l) =>
              l.id === 'menu-page' ? (
                <Link key={l.id} to="/menu-full" className={location.pathname === '/menu-full' ? 'is-active' : ''}>
                  {pt ? l.pt : l.en}
                </Link>
              ) : (
                <a key={l.id} href={l.id === 'home' ? '/' : `#${l.id}`} onClick={(e) => goTo(l.id, e)} className={isHome && active === l.id ? 'is-active' : ''}>
                  {pt ? l.pt : l.en}
                </a>
              ),
            )}
          </nav>

          <div className="flex items-center gap-3">
            <div className="ric-lang hidden items-center rounded-full border border-white/25 p-0.5 sm:flex" role="group" aria-label="Language">
              <button type="button" className={!pt ? 'on' : ''} onClick={() => setLang('en')} aria-pressed={!pt}>
                EN
              </button>
              <button type="button" className={pt ? 'on' : ''} onClick={() => setLang('pt')} aria-pressed={pt}>
                PT
              </button>
            </div>
            <a href="#booking" onClick={(e) => goTo('booking', e)} className="ric-book hidden items-center gap-2 px-5 py-3 md:inline-flex">
              <FaCalendarAlt /> {pt ? 'Reservar mesa' : 'Book a table'}
            </a>
            <button
              type="button"
              onClick={() => setMobileMenu(!mobileMenu)}
              className="flex h-11 w-11 flex-col items-center justify-center rounded-full border border-white/30 xl:hidden"
              aria-label={mobileMenu ? (pt ? 'Fechar menu' : 'Close menu') : pt ? 'Abrir menu' : 'Open menu'}
            >
              <span className={`block h-0.5 w-5 rounded-full bg-white transition-all duration-300 ${mobileMenu ? 'translate-y-[7px] rotate-45' : ''}`} />
              <span className={`my-[5px] block h-0.5 w-5 rounded-full bg-white transition-all duration-300 ${mobileMenu ? 'opacity-0' : ''}`} />
              <span className={`block h-0.5 w-5 rounded-full bg-white transition-all duration-300 ${mobileMenu ? '-translate-y-[7px] -rotate-45' : ''}`} />
            </button>
          </div>
        </div>

        <DrawerNuclear
          isOpen={mobileMenu}
          onClose={() => setMobileMenu(false)}
          logo={restaurantConfig.logoImage}
          language={language}
          toggleLanguage={toggleLanguage}
          handleReservation={() => goTo('booking')}
        />
      </header>

      {/* phones: booking always one tap away, hidden while the booking section is on screen */}
      <a
        href="#booking"
        onClick={(e) => goTo('booking', e)}
        className={`ric-pill ric-book fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 px-6 py-4 shadow-[0_12px_30px_-8px_rgba(0,0,0,0.55)] md:hidden ${
          bookingInView || mobileMenu || !pastHero ? 'is-hidden' : ''
        }`}
      >
        <FaCalendarAlt /> {pt ? 'Reservar mesa' : 'Book a table'}
      </a>
    </>
  );
};
